/**
 * Injects an iGrant.io-branded header (with a close button) into the
 * DocSearch modal whenever it opens. Pseudo-elements can't be interactive,
 * and we don't want to swizzle the whole DocSearch component, so this client
 * module observes for the modal node and prepends a real DOM header.
 *
 * The close button dispatches an Escape keydown which DocSearch's own
 * keyboard handler turns into a modal close.
 */

if (typeof window !== 'undefined') {
  const HEADER_CLASS = 'docsearch-drawer-header';

  function injectHeader(modal) {
    if (!modal || modal.querySelector(`.${HEADER_CLASS}`)) return;

    const header = document.createElement('div');
    header.className = HEADER_CLASS;

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = `${HEADER_CLASS}__close`;
    closeBtn.setAttribute('aria-label', 'Close search');
    closeBtn.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
      </svg>
    `;
    closeBtn.addEventListener('click', () => {
      // Dismiss the iOS keyboard first so it doesn't leave a viewport shift.
      if (document.activeElement && document.activeElement !== document.body) {
        document.activeElement.blur();
      }
      // DocSearch listens for Escape on document; dispatch it to close the modal.
      const escEvent = new KeyboardEvent('keydown', {
        key: 'Escape',
        code: 'Escape',
        keyCode: 27,
        which: 27,
        bubbles: true,
        cancelable: true,
      });
      document.dispatchEvent(escEvent);
    });

    const title = document.createElement('span');
    title.className = `${HEADER_CLASS}__title`;
    title.textContent = 'Search iGrant.io DevDocs';

    header.appendChild(title);
    header.appendChild(closeBtn);
    modal.insertBefore(header, modal.firstChild);

    // Override the default placeholder text on the search input.
    const input = modal.querySelector('.DocSearch-Input');
    if (input) {
      input.placeholder = 'Type and enter to search';
    }

    // Lock body scroll immediately - this is the most reliable trigger
    // because it fires the instant the modal DOM appears (no race).
    lockBodyScroll();

    // Inject filter chips between the search bar and the results dropdown.
    injectFilterChips(modal);

    // Watch the dropdown for result updates and categorize group headers.
    watchDropdown(modal);
  }

  // Watch for DocSearch container being added/removed from the DOM.
  // Process removals BEFORE additions so that when React batches both
  // into a single mutation (close old + open new), we unlock first
  // then re-lock - instead of locking then immediately unlocking.
  const observer = new MutationObserver((mutations) => {
    let containerAdded = null;
    let containerRemoved = false;

    for (const mutation of mutations) {
      for (const node of mutation.removedNodes) {
        if (node instanceof HTMLElement && node.classList.contains('DocSearch-Container')) {
          containerRemoved = true;
        }
      }
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement && node.classList.contains('DocSearch-Container')) {
          containerAdded = node;
        }
      }
    }

    // Process removal first, then addition (handles React batching)
    if (containerRemoved) {
      unlockBodyScroll();
      if (dropdownObserver) { dropdownObserver.disconnect(); dropdownObserver = null; }
    }
    if (containerAdded) {
      const modal = containerAdded.querySelector('.DocSearch-Modal');
      if (modal) {
        injectHeader(modal);
        lockBodyScroll();
      }
    }
  });

  // ── iOS Safari body scroll lock ──────────────────────────────────────────
  // iOS Safari allows the body behind a position:fixed overlay to scroll on
  // touch. We lock both <html> and <body> when the drawer opens and restore
  // the exact scroll position on close.
  //
  // The lock is triggered in TWO places for reliability (race-proof):
  //   1. injectHeader() - fires the instant the modal DOM appears
  //   2. The main MutationObserver - fires when the container is removed
  //      (unlock only)

  // No isLocked guard - calling lock/unlock multiple times is safe (idempotent).
  // Guards were the source of the "works first time, fails second time" bug:
  // if unlock didn't fire cleanly, isLocked stayed true and the next lock
  // was skipped entirely.

  // Minimal scroll lock: touch-action only. No overflow/position changes
  // (those cause viewport shifts on iOS Safari). The DocSearch overlay
  // blocks mouse interaction on desktop; touch-action blocks touch scroll
  // bleed on iOS.
  function lockBodyScroll() {
    document.documentElement.style.touchAction = 'none';
    document.body.style.touchAction = 'none';
  }

  function unlockBodyScroll() {
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
    document.documentElement.style.touchAction = '';
    document.body.style.touchAction = '';
  }

  // ── Category / sub-category labels for result group headers ────────────
  // DocSearch groups results under .DocSearch-Hit-source headers showing
  // the raw hierarchy.lvl0 value (e.g., "Get Started Digital Wallet
  // (OpenID4VC) Issuer"). We replace each with a two-line label: a small
  // category name above and the sub-category name below.

  const CATEGORY_RULES = [
    // [prefix to match, category name]
    // Order matters: longest/most-specific prefixes first within each group
    ['Get Started Digital Wallet (OpenID4VC) ', 'Developer APIs'],
    ['Get Started Extensions (Admin) ', 'Developer APIs'],
    ['Get Started Extensions ', 'Developer APIs'],
    ['Get Started Consent Management (Admin) ', 'Developer APIs'],
    ['Get Started Consent Management (Individuals) ', 'Developer APIs'],
    ['Get Started Consent Management ', 'Developer APIs'],
    ['Get Started Data Marketplace (Admin) ', 'Developer APIs'],
    ['Get Started Data Marketplace (Apps) ', 'Developer APIs'],
    ['Get Started Data Marketplace ', 'Developer APIs'],
    ['Get Started DRV Bund Controller APIs ', 'Developer APIs'],
    ['Get Started Developer Guides ', 'Developer Guide'],
    ['Get Started Product Offerings ', 'Developer Guide'],
    ['Get Started ', 'Developer Guide'],
    ['EUDI Wallet Insights ', 'Articles & Insights'],
    ['EUDI Wallet Insights', 'Articles & Insights'],
    ['Data Agreement Vocab', 'Articles & Insights'],
    ['Data Disclosure Agreement Vocab', 'Articles & Insights'],
    ['eIDAS & EU Regulations ', 'EU Regulations'],
    ['eIDAS & EU Regulations', 'EU Regulations'],
  ];

  function categorize(sourceText) {
    for (const [prefix, category] of CATEGORY_RULES) {
      if (sourceText.startsWith(prefix)) {
        const sub = sourceText.slice(prefix.length).trim();
        return { category, subcategory: sub || sourceText };
      }
    }
    return { category: 'Other', subcategory: sourceText };
  }

  function categorizeSourceHeaders(dropdown) {
    if (!dropdown) return;
    const headers = dropdown.querySelectorAll('.DocSearch-Hit-source');
    for (const header of headers) {
      // Skip only if our spans are still present. React re-renders the
      // result list (on hover / arrow-key selection state changes) and
      // replaces our span children with the raw text again, so we must
      // detect that by checking the actual DOM - not a flag React leaves
      // untouched.
      if (header.firstElementChild?.classList.contains('docsearch-source-category')) continue;

      const raw = header.textContent.trim();
      const { category, subcategory } = categorize(raw);

      header.innerHTML =
        '<span class="docsearch-source-category">' +
        category.replace(/&/g, '&amp;').replace(/</g, '&lt;') +
        '</span>' +
        '<span class="docsearch-source-subcategory">' +
        subcategory.replace(/&/g, '&amp;').replace(/</g, '&lt;') +
        '</span>';
    }
  }

  // Observe the dropdown for child-list changes (new results rendered).
  let dropdownObserver = null;

  function watchDropdown(modal) {
    const dropdown = modal.querySelector('.DocSearch-Dropdown');
    if (!dropdown) return;

    // Process any headers already present
    categorizeSourceHeaders(dropdown);

    // Watch for new results (e.g., when the user types a new query)
    if (dropdownObserver) dropdownObserver.disconnect();
    dropdownObserver = new MutationObserver(() => {
      categorizeSourceHeaders(dropdown);
    });
    dropdownObserver.observe(dropdown, { childList: true, subtree: true });
  }

  // ── Category filter chips (server-side filter_by) ──────────────────────
  // The chip UI lives between the search input and the results dropdown.
  // Clicking a chip sets a module-level state flag that the XHR monkey-patch
  // reads, injecting a `filter_by` expression into outgoing Typesense
  // multi_search requests. Re-search is triggered by round-tripping the
  // input's value through a native-setter clear+restore so React sees a
  // change event and the DocSearch store issues a new query.
  //
  // Why filter on hierarchy.lvl0 (not a dedicated `category` field):
  //   hierarchy.lvl0 already exists in the scraped schema (populated from
  //   the active sidebar/nav label on each page), so filter_by works
  //   against the live collection with zero re-index. Adding a separate
  //   `category` field would require a scraper config + re-index cycle
  //   before any filter returns results.
  //
  // Adding a new category:
  //   1. Add a chip entry below with its filterBy expression (OR across
  //      the hierarchy.lvl0 substrings that identify its pages).
  //   2. That's it - no server-side changes.
  //
  // Adding a new sidebar group under an existing category:
  //   extend that category's filterBy substrings to include the new
  //   hierarchy.lvl0 prefix. Otherwise its pages show only under "All".

  const FILTER_CHIPS = [
    {
      id: 'all',
      label: 'All',
      filterBy: null,
    },
    {
      id: 'apis',
      label: 'APIs',
      filterBy:
        'hierarchy.lvl0:`Get Started Digital Wallet` || ' +
        'hierarchy.lvl0:`Get Started Extensions` || ' +
        'hierarchy.lvl0:`Get Started Consent Management` || ' +
        'hierarchy.lvl0:`Get Started Data Marketplace` || ' +
        'hierarchy.lvl0:`Get Started DRV Bund`',
    },
    {
      id: 'developer-guide',
      label: 'Developer Guide',
      filterBy:
        'hierarchy.lvl0:`Get Started Developer Guides` || ' +
        'hierarchy.lvl0:`Get Started Product Offerings`',
    },
    {
      id: 'articles',
      label: 'Articles',
      filterBy:
        'hierarchy.lvl0:`EUDI Wallet Insights` || ' +
        'hierarchy.lvl0:`Data Agreement Vocab` || ' +
        'hierarchy.lvl0:`Data Disclosure Agreement Vocab`',
    },
    {
      id: 'eu-regulations',
      label: 'EU Regulations',
      filterBy: 'hierarchy.lvl0:`eIDAS`',
    },
  ];

  let activeFilter = 'all'; // 'all' = no filter_by applied

  function getActiveFilterBy() {
    if (activeFilter === 'all') return null;
    const chip = FILTER_CHIPS.find(c => c.id === activeFilter);
    return chip ? chip.filterBy : null;
  }

  function injectFilterChips(modal) {
    if (modal.querySelector('.docsearch-filters')) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'docsearch-filters';
    wrapper.setAttribute('role', 'tablist');
    wrapper.setAttribute('aria-label', 'Filter search results by category');

    for (const chip of FILTER_CHIPS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'docsearch-filter-chip';
      btn.dataset.filter = chip.id;
      btn.setAttribute('role', 'tab');
      btn.textContent = chip.label;
      if (chip.id === activeFilter) {
        btn.classList.add('docsearch-filter-chip--active');
        btn.setAttribute('aria-selected', 'true');
      } else {
        btn.setAttribute('aria-selected', 'false');
      }
      btn.addEventListener('click', () => setActiveFilter(chip.id, modal));
      wrapper.appendChild(btn);
    }

    // Insert right after the search bar, before the dropdown.
    const searchBar = modal.querySelector('.DocSearch-SearchBar');
    if (searchBar && searchBar.nextSibling) {
      modal.insertBefore(wrapper, searchBar.nextSibling);
    } else {
      modal.appendChild(wrapper);
    }
  }

  function setActiveFilter(id, modal) {
    if (id === activeFilter) return;
    activeFilter = id;

    // Update chip styling
    const chips = modal.querySelectorAll('.docsearch-filter-chip');
    for (const c of chips) {
      const isActive = c.dataset.filter === id;
      c.classList.toggle('docsearch-filter-chip--active', isActive);
      c.setAttribute('aria-selected', isActive ? 'true' : 'false');
    }

    // Trigger DocSearch to re-query. React ignores same-value writes, so we
    // round-trip through the native value setter: clear → restore. The
    // DocSearch store re-issues the query on the restore, and our XHR
    // monkey-patch now injects the new filter_by.
    const input = modal.querySelector('.DocSearch-Input');
    if (!input || !input.value) return;
    const nativeSet = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value',
    ).set;
    const current = input.value;
    nativeSet.call(input, '');
    input.dispatchEvent(new Event('input', { bubbles: true }));
    nativeSet.call(input, current);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // ── XHR monkey-patch: inject filter_by into Typesense multi_search ─────
  // Scoped to the Typesense host + /multi_search path so we don't touch
  // unrelated XHRs. Runs once at module load. The patch reads `activeFilter`
  // at request time, so chip toggles immediately affect the next query.
  //
  // Before the scraper re-indexes with the `category` field, filter_by will
  // return 0 results - the "All" chip keeps working since we only inject
  // filter_by when a non-"all" chip is selected.

  (function installTypesenseFilterPatch() {
    const OriginalOpen = window.XMLHttpRequest.prototype.open;
    const OriginalSend = window.XMLHttpRequest.prototype.send;

    window.XMLHttpRequest.prototype.open = function patchedOpen(method, url) {
      try {
        if (
          typeof url === 'string' &&
          url.includes('/multi_search') &&
          url.includes('typesense')
        ) {
          this.__isTypesenseMultiSearch = true;

          // Register the error-rewrite listener HERE in open(), before the
          // caller (typesense-js / axios) registers its own readystatechange
          // handler between open() and send(). Event listeners fire in
          // registration order, so we must be first in the queue - otherwise
          // the adapter reads the original error-shaped response and crashes
          // before our rewrite has a chance to run. send() just sets a flag
          // (__rewriteOnError) to opt-in per-request when filter_by was
          // injected.
          this.addEventListener('readystatechange', function rewriteOnDone() {
            if (this.readyState !== 4) return;
            if (!this.__rewriteOnError) return;
            try {
              const raw = this.responseText;
              if (!raw) return;
              const parsed = JSON.parse(raw);
              if (!parsed || !Array.isArray(parsed.results)) return;
              if (!parsed.results.some(r => r && r.error)) return;
              // The empty-hits shim must have every field the adapter reads:
              //   hits              → _adaptHits
              //   found, out_of     → _adaptNumberOfHits / _adaptNumberOfPages
              //   request_params.per_page → _adaptNumberOfPages
              //   facet_counts      → _adaptFacets
              const fixed = {
                ...parsed,
                results: parsed.results.map(r =>
                  (r && r.error)
                    ? {
                        hits: [],
                        found: 0,
                        out_of: 0,
                        page: 1,
                        search_time_ms: 0,
                        facet_counts: [],
                        request_params: {
                          collection_name: '',
                          q: '',
                          per_page: 20,
                        },
                      }
                    : r,
                ),
              };
              const newJson = JSON.stringify(fixed);
              Object.defineProperty(this, 'responseText', {
                get: () => newJson,
                configurable: true,
              });
              Object.defineProperty(this, 'response', {
                get: () => newJson,
                configurable: true,
              });
            } catch (_) { /* leave original response alone */ }
          });
        }
      } catch (_) { /* never let the patch break real XHR */ }
      return OriginalOpen.apply(this, arguments);
    };

    window.XMLHttpRequest.prototype.send = function patchedSend(body) {
      let filterInjected = false;
      try {
        const activeFilterBy = getActiveFilterBy();
        if (
          this.__isTypesenseMultiSearch &&
          activeFilterBy &&
          typeof body === 'string' &&
          body.length > 0
        ) {
          const payload = JSON.parse(body);
          if (payload && Array.isArray(payload.searches)) {
            // Wrap the active filter in parens so it composes safely with
            // any existing filter_by (e.g. the contextual docusaurus_tag
            // filter DocSearch adds). OR-logic at the top level of the
            // active filter stays grouped instead of binding loosely to
            // the left-hand side.
            const clause = '(' + activeFilterBy + ')';
            for (const s of payload.searches) {
              s.filter_by = s.filter_by ? s.filter_by + ' && ' + clause : clause;
            }
            arguments[0] = JSON.stringify(payload);
            filterInjected = true;
          }
        }
      } catch (_) { /* fall through and send the original body */ }

      // Opt this request into the error-rewrite listener we registered in
      // open(). Even though hierarchy.lvl0 is in the live schema, we keep
      // the safety net for any future filter expression that might
      // reference a missing field - fail-soft instead of crashing the UI.
      if (filterInjected) {
        this.__rewriteOnError = true;
      }

      return OriginalSend.apply(this, arguments);
    };
  })();

  // Start observing once the body exists.
  function start() {
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });

      // Handle the case where the modal is already open at the time this runs.
      const existing = document.querySelector('.DocSearch-Modal');
      if (existing) injectHeader(existing);
    } else {
      requestAnimationFrame(start);
    }
  }

  start();
}

// Docusaurus client modules export an object; we don't need any lifecycle hooks.
export default {};
